import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoService } from '@jsverse/transloco';
import { Observable, Subscription, of } from 'rxjs';
import {
  DatasetProfileDto,
  MINIMUM_REQUIREMENTS,
  createDefaultMultiMaterialProfile,
  createDefaultPackagingComponentProfile,
  createDefaultPackagingSystemProfile,
  createDefaultPackagingUnitProfile,
} from 'src/app/data-transfer/entities/doc-entities/dataset-profile-dto';
import { ProfileSaveResultDto } from 'src/app/data-transfer/entities/doc-entities/profile-save-result-dto';
import { CustomFieldDto } from 'src/app/data-transfer/entities/custom-field-dto';
import { PpwrApiService } from 'src/app/data-transfer/services/ppwr-api.service';
import { CustomFieldApiService } from 'src/app/data-transfer/services/custom-field-api-service';
import { CoreDataUpdateService } from 'src/app/data-transfer/services/core-data-update-api-service';
import { DeleteItemDialogComponent } from 'src/app/components/dialogs/delete-item-dialog/delete-item-dialog.component';
import { TdProfileDialogComponent, TdProfileDialogData } from 'src/app/components/dialogs/td-profile-dialog/td-profile-dialog.component';
import { DialogActions } from 'src/app/model/dictionary';
import { getDialogConfig } from 'src/app/util/dialog-util';

interface DatasetProfileConfig {
  key: string;
  tabLabelKey: string;
  listAll: () => Observable<DatasetProfileDto[]>;
  createOrUpdate: (profile: DatasetProfileDto) => Observable<ProfileSaveResultDto>;
  deleteProfile: (id: string, version?: number) => Observable<void>;
  createDefault: () => DatasetProfileDto;
  getAvailableCustomFields: () => Observable<CustomFieldDto[]>;
}

interface DatasetProfileState {
  config: DatasetProfileConfig;
  profiles: DatasetProfileDto[];
  availableCustomFields: CustomFieldDto[];
}

@Component({
  selector: 'app-td-profile-panel',
  templateUrl: './td-profile-panel.component.html',
  styleUrls: ['./td-profile-panel.component.scss'],
})
export class TdProfilePanelComponent implements OnInit, OnDestroy {
  states: DatasetProfileState[] = [];

  private dataSubscription?: Subscription;

  constructor(
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private translocoService: TranslocoService,
    private ppwrApiService: PpwrApiService,
    private customFieldApiService: CustomFieldApiService,
    private coreDataService: CoreDataUpdateService
  ) {
    this.states = this.buildConfigs().map(config => this.createState(config));
  }

  get evidenceTypes() {
    return this.coreDataService.allDOCEvidenceTypes;
  }

  ngOnInit(): void {
    this.dataSubscription = new Subscription();
    this.states.forEach(state => {
      const subscription = state.config.listAll().subscribe({
        next: profiles => (state.profiles = profiles),
        error: error => {
          console.error(`Error while loading ${state.config.key} profile data:`, error);
          this.showSnackbar('ppwr.tdProfiles.loadError', 'app-error', 4000);
        },
      });
      this.dataSubscription?.add(subscription);

      const customFieldsSubscription = state.config.getAvailableCustomFields().subscribe({
        next: fields => (state.availableCustomFields = fields),
        error: error => console.error(`Error while loading ${state.config.key} custom fields:`, error),
      });
      this.dataSubscription?.add(customFieldsSubscription);
    });
  }

  ngOnDestroy(): void {
    this.dataSubscription?.unsubscribe();
  }

  private buildConfigs(): DatasetProfileConfig[] {
    return [
      {
        key: 'system',
        tabLabelKey: 'ppwr.tdProfiles.datasetTab.system',
        listAll: () => this.ppwrApiService.getAllPackagingSystemProfiles(),
        createOrUpdate: profile => this.ppwrApiService.createOrUpdatePackagingSystemProfile(profile),
        deleteProfile: (id, version) => this.ppwrApiService.deletePackagingSystemProfile(id, version),
        createDefault: createDefaultPackagingSystemProfile,
        getAvailableCustomFields: () => this.customFieldApiService.getPackagingSystemFields(),
      },
      {
        key: 'unit',
        tabLabelKey: 'ppwr.tdProfiles.datasetTab.unit',
        listAll: () => this.ppwrApiService.getAllPackagingUnitProfiles(),
        createOrUpdate: profile => this.ppwrApiService.createOrUpdatePackagingUnitProfile(profile),
        deleteProfile: (id, version) => this.ppwrApiService.deletePackagingUnitProfile(id, version),
        createDefault: createDefaultPackagingUnitProfile,
        getAvailableCustomFields: () => this.customFieldApiService.getPackagingUnitFields(),
      },
      {
        key: 'component',
        tabLabelKey: 'dataManagement.import.packagingComponent',
        listAll: () => this.ppwrApiService.getAllPackagingComponentProfiles(),
        createOrUpdate: profile => this.ppwrApiService.createOrUpdatePackagingComponentProfile(profile),
        deleteProfile: (id, version) => this.ppwrApiService.deletePackagingComponentProfile(id, version),
        createDefault: createDefaultPackagingComponentProfile,
        getAvailableCustomFields: () => this.customFieldApiService.getPackagingComponentFields(),
      },
      {
        key: 'multiMaterial',
        tabLabelKey: 'dataManagement.import.compositeMaterial',
        listAll: () => this.ppwrApiService.getAllMultiMaterialProfiles(),
        createOrUpdate: profile => this.ppwrApiService.createOrUpdateMultiMaterialProfile(profile),
        deleteProfile: (id, version) => this.ppwrApiService.deleteMultiMaterialProfile(id, version),
        createDefault: createDefaultMultiMaterialProfile,
        getAvailableCustomFields: () => of([]),
      },
    ];
  }

  private createState(config: DatasetProfileConfig): DatasetProfileState {
    return {
      config,
      profiles: [],
      availableCustomFields: [],
    };
  }

  profilesFor(key: string): DatasetProfileDto[] {
    return this.states.find(state => state.config.key === key)?.profiles ?? [];
  }

  minimumRequirementsFor(state: DatasetProfileState): string[] {
    return MINIMUM_REQUIREMENTS[state.config.key] ?? [];
  }

  addProfile(state: DatasetProfileState): void {
    this.openDialog(state, state.config.createDefault(), true);
  }

  editProfile(state: DatasetProfileState, profile: DatasetProfileDto): void {
    this.openDialog(state, profile, false);
  }

  private openDialog(state: DatasetProfileState, profile: DatasetProfileDto, isEditingNew: boolean): void {
    const data = new TdProfileDialogData(
      state.config.key,
      state.config.tabLabelKey,
      profile,
      isEditingNew,
      state.profiles.filter(p => p.id !== profile.id).map(p => p.profileName),
      this.evidenceTypes,
      state.availableCustomFields,
      this.minimumRequirementsFor(state),
      this.profilesFor('unit'),
      this.profilesFor('component'),
      this.profilesFor('multiMaterial')
    );

    this.dialog
      .open(TdProfileDialogComponent, this.getPpwrDialogConfig(data, '1200px'))
      .afterClosed()
      .subscribe(result => {
        if (result?.event !== DialogActions.CONFIRM) return;
        this.persistProfile(state, result.data);
      });
  }

  private persistProfile(state: DatasetProfileState, candidate: DatasetProfileDto): void {
    const originalId = candidate.id;
    state.config.createOrUpdate(candidate).subscribe({
      next: saveResult => {
        const persistedProfile: DatasetProfileDto = { ...candidate, id: saveResult.id, version: saveResult.version };
        const index = state.profiles.findIndex(p => p.id === originalId);
        state.profiles = index === -1 ? [...state.profiles, persistedProfile] : state.profiles.map((p, i) => (i === index ? persistedProfile : p));
      },
      error: error => this.handleSaveError(error),
    });
  }

  private handleSaveError(error: unknown): void {
    console.error('Error while saving profile:', error);
    this.showSnackbar('ppwr.tdProfiles.saveError', 'app-error', 4000);
  }

  confirmDeleteProfile(state: DatasetProfileState, profile: DatasetProfileDto): void {
    const dialogData = {
      dialogHeader: this.translocoService.translate('ppwr.tdProfiles.deleteHeader'),
      dialogText: this.translocoService.translate('ppwr.tdProfiles.deleteText'),
    };
    this.dialog
      .open(DeleteItemDialogComponent, this.getPpwrDialogConfig(dialogData, '500px'))
      .afterClosed()
      .subscribe(result => {
        if (result?.event !== DialogActions.DELETE) return;
        state.config.deleteProfile(profile.id, profile.version).subscribe({
          next: () => {
            state.profiles = state.profiles.filter(p => p.id !== profile.id);
          },
          error: error => {
            console.error('Error while deleting profile:', error);
            this.showSnackbar(this.extractConflictMessage(error, profile) ?? 'ppwr.tdProfiles.deleteError', 'app-error', 4000);
          },
        });
      });
  }

  private showSnackbar(messageKey: string, panelClass: string, duration: number): void {
    this.snackBar.open(this.translocoService.translate(messageKey), this.translocoService.translate('common.buttons.close'), {
      duration,
      panelClass: ['app-snackbar', panelClass],
    });
  }

  private extractConflictMessage(error: unknown, profile: DatasetProfileDto): string | null {
    if (!(error instanceof HttpErrorResponse) || error.status !== 400 || typeof error.error !== 'string') return null;
    return error.error.replaceAll(`with ID ${profile.id}`, `with the name ${profile.profileName}`);
  }

  private getPpwrDialogConfig(data: unknown, width: string): MatDialogConfig {
    const config = getDialogConfig(data, width);
    config.maxWidth = 'calc(100vw - 32px)';
    config.panelClass = 'ppwr-dialog-panel';
    config.restoreFocus = true;
    return config;
  }
}
